import { useState, useMemo } from "react";
import {
  Sun, Zap, Leaf, ChevronRight, Plug, ShieldCheck,
  Wrench, Home, MapPin, CircleDollarSign, TrendingDown,
  PanelTop, BatteryCharging,
} from "lucide-react";

const SOLAR_IRRADIANCE = {
  "Abruzzo": 1350, "Basilicata": 1500, "Calabria": 1550, "Campania": 1450,
  "Emilia-Romagna": 1250, "Friuli Venezia Giulia": 1200, "Lazio": 1400,
  "Liguria": 1300, "Lombardia": 1200, "Marche": 1300, "Molise": 1400,
  "Piemonte": 1200, "Puglia": 1550, "Sardegna": 1600, "Sicilia": 1650,
  "Toscana": 1350, "Trentino-Alto Adige": 1150, "Umbria": 1350,
  "Valle d'Aosta": 1150, "Veneto": 1220,
};

const KIT_WATT = 820;
const ELECTRICITY_PRICE = 0.25;
const SELF_CONSUMPTION = 0.70;

const regions = Object.keys(SOLAR_IRRADIANCE).sort();

function calcBalcony(region) {
  const irr = SOLAR_IRRADIANCE[region] || 1350;
  const prod = Math.round(KIT_WATT / 1000 * irr);
  const self = Math.round(prod * SELF_CONSUMPTION);
  const annual = Math.round(self * ELECTRICITY_PRICE);
  let total20 = 0;
  for (let y = 0; y < 20; y++) total20 += annual * Math.pow(0.996, y);
  return { prod, self, annual, total20: Math.round(total20), co2: Math.round(prod * 0.35) / 1000 };
}

export const ResidentialSolarChoice = ({ onRooftopClick, onBalconyClick }) => {
  const [activeTab, setActiveTab] = useState("tetto");
  const [region, setRegion] = useState("Lazio");
  const balcony = useMemo(() => calcBalcony(region), [region]);

  return (
    <section className="py-16 sm:py-24 bg-white" data-testid="solar-choice-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wide">Soluzioni Fotovoltaiche</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="solar-choice-title">
            Quale soluzione fa per te?
          </h2>
          <p className="mt-4 text-slate-500 text-base sm:text-lg">
            Scegli il tipo di impianto piu adatto alla tua abitazione e scopri subito quanto puoi risparmiare.
          </p>
        </div>

        {/* Tab Selector */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-slate-100 rounded-2xl p-1.5 gap-1" data-testid="solar-choice-tabs">
            <button
              onClick={() => setActiveTab("tetto")}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === "tetto" ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/25" : "text-slate-500 hover:text-slate-700"}`}
              data-testid="solar-tab-tetto"
            >
              <Home className="w-4 h-4" /> Fotovoltaico da Tetto
            </button>
            <button
              onClick={() => setActiveTab("balcone")}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all ${activeTab === "balcone" ? "bg-amber-500 text-white shadow-lg shadow-amber-500/25" : "text-slate-500 hover:text-slate-700"}`}
              data-testid="solar-tab-balcone"
            >
              <Sun className="w-4 h-4" /> Fotovoltaico da Balcone
            </button>
          </div>
        </div>

        {/* TETTO Panel */}
        {activeTab === "tetto" && (
          <div className="animate-fade-in" data-testid="solar-panel-tetto">
            <div className="bg-gradient-to-br from-emerald-50 to-white rounded-3xl border-2 border-emerald-200 overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Left - Info */}
                <div className="p-8 sm:p-10 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 text-xs font-bold px-3 py-1.5 rounded-full w-fit mb-5">
                    <PanelTop className="w-3.5 h-3.5" /> Impianto Completo
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Impianto Fotovoltaico da Tetto
                  </h3>
                  <p className="text-slate-500 leading-relaxed mb-6">
                    La soluzione completa per massimizzare il risparmio energetico. Pannelli ad alta efficienza, inverter e batteria di accumulo per un autoconsumo fino al 90%.
                  </p>
                  <div className="grid grid-cols-2 gap-3 mb-8">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                        <BatteryCharging className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">Accumulo</div>
                        <div className="text-xs text-slate-500">Batteria integrata</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                        <Zap className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">3-10+ kWp</div>
                        <div className="text-xs text-slate-500">Potenza modulare</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                        <CircleDollarSign className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">Fino a 90%</div>
                        <div className="text-xs text-slate-500">Autoconsumo</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                        <TrendingDown className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">4-6 anni</div>
                        <div className="text-xs text-slate-500">Rientro investimento</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button onClick={onRooftopClick}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-7 py-3 text-sm btn-scale shadow-lg inline-flex items-center justify-center gap-2 transition-colors"
                      data-testid="solar-tetto-cta">
                      Configura il Tuo Impianto <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Right - Visual */}
                <div className="bg-emerald-100/50 p-8 sm:p-10 flex flex-col items-center justify-center text-center">
                  <div className="w-24 h-24 rounded-3xl bg-emerald-500 flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/30">
                    <PanelTop className="w-12 h-12 text-white" strokeWidth={1.5} />
                  </div>
                  <div className="text-4xl font-bold text-emerald-700 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Fino a €2.500</div>
                  <div className="text-sm text-emerald-600 font-medium">risparmio annuo con batteria</div>
                  <div className="mt-6 grid grid-cols-3 gap-4 w-full max-w-xs">
                    <div>
                      <div className="text-lg font-bold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>25+</div>
                      <div className="text-[10px] text-slate-500">anni di vita</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>50%</div>
                      <div className="text-[10px] text-slate-500">detrazione</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>SSP</div>
                      <div className="text-[10px] text-slate-500">scambio sul posto</div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-4">Casa indipendente, villetta, villa</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* BALCONE Panel */}
        {activeTab === "balcone" && (
          <div className="animate-fade-in" data-testid="solar-panel-balcone">
            <div className="bg-gradient-to-br from-amber-50 to-white rounded-3xl border-2 border-amber-200 overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Left - Info + Benefits */}
                <div className="p-8 sm:p-10 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1.5 rounded-full w-fit mb-5">
                    <Plug className="w-3.5 h-3.5" /> Plug & Play 820W
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Kit Fotovoltaico da Balcone
                  </h3>
                  <p className="text-slate-500 leading-relaxed mb-6">
                    La soluzione ideale per chi vive in appartamento. Si installa in pochi minuti sul balcone, senza lavori e senza permessi edilizi.
                  </p>
                  <div className="space-y-3 mb-8">
                    {[
                      { icon: Plug, text: "Plug & Play: colleghi e produci subito" },
                      { icon: ShieldCheck, text: "Nessuna pratica edilizia necessaria" },
                      { icon: Wrench, text: "Installazione fai-da-te in 30 minuti" },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                          <item.icon className="w-3.5 h-3.5 text-amber-600" />
                        </div>
                        <span className="text-sm text-slate-700 font-medium">{item.text}</span>
                      </div>
                    ))}
                  </div>
                  <button onClick={onBalconyClick}
                    className="bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-full px-7 py-3 text-sm btn-scale shadow-lg inline-flex items-center justify-center gap-2 transition-colors w-fit"
                    data-testid="solar-balcone-cta">
                    Richiedi Info Kit Balcone <ChevronRight className="w-4 h-4" />
                  </button>
                  <p className="text-xs text-slate-400 mt-3">Ideale per appartamenti con balcone</p>
                </div>

                {/* Right - Calculator */}
                <div className="bg-amber-50/70 p-6 sm:p-8" data-testid="balcony-calculator">
                  <h4 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                    <Sun className="w-4 h-4 text-amber-500" /> Calcola il Tuo Risparmio
                  </h4>

                  <div className="mb-5">
                    <label className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-amber-500" /> La tua regione
                    </label>
                    <select value={region} onChange={(e) => setRegion(e.target.value)}
                      className="flex h-11 w-full rounded-xl border border-amber-200 bg-white px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                      data-testid="balcony-region-select">
                      {regions.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-4" data-testid="balcony-results">
                    <div className="bg-white rounded-xl p-4 text-center border border-amber-100">
                      <Sun className="w-5 h-5 text-amber-500 mx-auto mb-1.5" strokeWidth={1.5} />
                      <div className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="balcony-production">
                        {balcony.prod.toLocaleString("it-IT")}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">kWh/anno</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center border border-emerald-100">
                      <CircleDollarSign className="w-5 h-5 text-emerald-500 mx-auto mb-1.5" strokeWidth={1.5} />
                      <div className="text-xl font-bold text-emerald-600" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="balcony-annual-savings">
                        {"\u20AC"}{balcony.annual}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">risparmio/anno</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center border border-blue-100">
                      <TrendingDown className="w-5 h-5 text-blue-500 mx-auto mb-1.5" strokeWidth={1.5} />
                      <div className="text-xl font-bold text-blue-600" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="balcony-total-savings">
                        {"\u20AC"}{balcony.total20.toLocaleString("it-IT")}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">in 20 anni</div>
                    </div>
                    <div className="bg-white rounded-xl p-4 text-center border border-green-100">
                      <Leaf className="w-5 h-5 text-green-500 mx-auto mb-1.5" strokeWidth={1.5} />
                      <div className="text-xl font-bold text-green-600" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="balcony-co2">
                        {balcony.co2 > 0 ? balcony.co2.toFixed(1) : "<0.1"}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">ton CO{"\u2082"}/anno</div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-100 p-3 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] text-slate-400">Autoconsumo</div>
                      <div className="text-sm font-bold text-amber-600">~70%</div>
                    </div>
                    <div className="h-8 w-px bg-slate-100" />
                    <div>
                      <div className="text-[10px] text-slate-400">Mensile</div>
                      <div className="text-sm font-bold text-slate-900">{"\u20AC"}{Math.round(balcony.annual / 12)}/mese</div>
                    </div>
                    <div className="h-8 w-px bg-slate-100" />
                    <div>
                      <div className="text-[10px] text-slate-400">Potenza</div>
                      <div className="text-sm font-bold text-amber-600">820W</div>
                    </div>
                  </div>

                  <p className="text-[10px] text-slate-400 mt-3 leading-relaxed">
                    * Stime basate su autoconsumo ~70%, {"\u20AC"}0,25/kWh. Risultati variabili in base a esposizione e abitudini.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
